import { useState } from "react"
import type { Clarify } from "../contract"
import { Beam } from "../ds"

const OTHER = "__other__"

interface ClarifyCardProps {
  id: string
  clarify: Clarify
  /** Continue: one value per field name. */
  onAnswers: (answers: Record<string, string>) => void
  /** "Answer anyway with assumptions". */
  onAssume: () => void
}

/** "One detail changes this answer": one-tap options per field, Continue, or answer on assumptions. */
export function ClarifyCard({ id, clarify, onAnswers, onAssume }: ClarifyCardProps) {
  const [picked, setPicked] = useState<Record<string, string>>({})
  const [other, setOther] = useState<Record<string, string>>({})

  const valueOf = (name: string) => {
    const p = picked[name]
    return p === OTHER || p === undefined ? (other[name] ?? "").trim() : p
  }
  const complete = clarify.fields.every((f) => valueOf(f.name) !== "")

  return (
    <article className="card nira-run" id={id}>
      <div className="chead">
        <Beam state="listening" size="hero" />
        <div>
          <p className="ov">NEEDS ONE DETAIL</p>
          <h3>{clarify.title}</h3>
        </div>
      </div>
      {clarify.why ? <p className="tk">{clarify.why}</p> : null}
      <div className="cbd">
        {clarify.fields.map((f) => {
          const showOther = f.allow_other && (f.options.length === 0 || picked[f.name] === OTHER)
          return (
            <fieldset className="fs" key={f.name}>
              <legend className="lab">{f.label}</legend>
              {f.options.length ? (
                <div className="pills" role="group" aria-label={f.label}>
                  {f.options.map((o) => (
                    <button key={o} type="button" className="pill" aria-pressed={picked[f.name] === o}
                      onClick={() => setPicked((s) => ({ ...s, [f.name]: o }))}>
                      {o}
                    </button>
                  ))}
                  {f.allow_other ? (
                    <button type="button" className="pill" aria-pressed={picked[f.name] === OTHER}
                      onClick={() => setPicked((s) => ({ ...s, [f.name]: OTHER }))}>
                      Other…
                    </button>
                  ) : null}
                </div>
              ) : null}
              {showOther ? (
                <input
                  className="oth"
                  type="text"
                  aria-label={f.options.length ? `Other: ${f.label}` : f.label}
                  placeholder="Type it here"
                  value={other[f.name] ?? ""}
                  onChange={(e) => setOther((s) => ({ ...s, [f.name]: e.target.value }))}
                />
              ) : null}
            </fieldset>
          )
        })}
        <div className="cacts">
          <button type="button" className="pbtn" disabled={!complete}
            onClick={() => onAnswers(Object.fromEntries(clarify.fields.map((f) => [f.name, valueOf(f.name)])))}>
            Continue
          </button>
          {clarify.can_assume ? (
            <button type="button" className="sbtn" onClick={onAssume}>Answer anyway with assumptions</button>
          ) : null}
        </div>
      </div>
    </article>
  )
}
